import { isUserPrompt } from "@1js/pl-types";
import * as React from "react";
import type { Appearance, Prompt } from "../data";
import type { ColumnSize } from "../Grid/Column";
import type {
  Filter,
  NoCustomerRelatedFilterDimension,
  NoCustomerRelatedFilterValue,
} from "../PromptFilters/types";
import {
  savedPromptsReducer,
  type SavedPromptsAction,
  type SavedPromptsState,
} from "../PromptResults/SavedPrompts/SavedPrompts.data";
import {
  suggestedPromptsReducer,
  type SuggestedPromptsAction,
  type SuggestedPromptsState,
} from "../PromptResults/SuggestedPrompts/SuggestedPrompts.data";
import {
  teamPromptsReducer,
  type TeamPromptsAction,
  type TeamPromptsState,
} from "../PromptResults/TeamPrompts/TeamPrompts.data";
import type { PromptTabs } from "../PromptTab/PromptTab";

export type PromptLibraryCommonState = {
  allPrompts: Prompt[];
  selectedTab: PromptTabs;
  filters: Filter[];
  isLoading?: boolean;
  appearance: Appearance;
  columnSize: ColumnSize;
  extensionIconMap?: Record<string, string>;

  isPromptSavingEnabled?: boolean;
  isFavoriteEnabled?: boolean;
  isPromptEditingEnabled?: boolean;
  isPromptSharingEnabled?: boolean;
  isPromptSharingOneClickUxEnabled?: boolean;
  isPromptSharingViaEmailEnabled?: boolean;
  isPromptRingTagEnabled?: boolean;
  isPromptSharingToWorkgroupEnabled?: boolean;
  isPromptRemovingFromWorkgroupEnabled?: boolean;
  isWorkgroupTabEnabled?: boolean;
  isDeclarativeCopilotSelected?: boolean;
  isPromptGalleryTitleEnabled?: boolean;

  onPromptClicked: (prompt: Prompt, displayPosition?: number) => void;
  onFavoriteToggled: (prompt: Prompt, favorited: boolean) => void;
  onEditToggled?: (prompt: Prompt) => void;
  onEditSaved?: (prompt: Prompt) => void;
  onShareViaCopyLinkClicked?: (prompt: Prompt) => void;
  onShareViaEmailClicked?: (prompt: Prompt) => Promise<void> | void;
  onShowOnlyFavoriteToggled?: (favorited: boolean) => void;
  onRemoveFromWorkgroupClicked?: (
    prompts: Prompt[],
    groupIds: string[]
  ) => void;
  onShareToWorkgroupClicked?: (prompts: Prompt[]) => void;
  onFilterChanged?: (
    filters: Record<
      NoCustomerRelatedFilterDimension,
      NoCustomerRelatedFilterValue[]
    >
  ) => void;
  onDelete?: (prompt: Prompt, undoDelete: () => void) => void;
  onShowOnlySavedToggled?: (saved: boolean) => void;
  onTabSelected?: (selectedTab: PromptTabs) => void;
};

export type PromptLibraryState = {
  common: PromptLibraryCommonState;
  suggested: SuggestedPromptsState;
  saved: SavedPromptsState;
  team: TeamPromptsState;
};
export type CommonAction =
  | {
      type: "SET_LOADING";
      payload: {
        isLoading: boolean;
      };
    }
  | {
      type: "SET_FILTERS";
      payload: {
        filters: Filter[];
      };
    }
  | {
      type: "SET_PROMPTS";
      payload: { prompts: Prompt[] };
    }
  | {
      type: "UPDATE_SELECTED_TAB";
      payload: { selectedTab: PromptTabs };
    }
  | {
      type: "DELETE_UNDO";
      payload: { prompt: Prompt; promptPosition: number };
    }
  | {
      type: "DELETE_SUCCEED";
      payload: { prompt: Prompt };
    };

export type PromptLibraryAction =
  | CommonAction
  | SuggestedPromptsAction
  | SavedPromptsAction
  | TeamPromptsAction;

export const DisplayCategoryKey = "displayCategory";
export const ProductNameKey = "products/name";
export const JobTypeKey = "jobType";
export const CreatedByKey = "createdBy";
export const ExtensionKey = "extension";
export const WorkgroupKey = "workgroup";

export const commonReducer = (
  state: PromptLibraryState,
  action: CommonAction
): PromptLibraryCommonState => {
  switch (action.type) {
    case "SET_PROMPTS": {
      // we always want to display the user-authored prompts before Microsoft created ones
      const userAuthoredPrompts = action.payload.prompts.filter(isUserPrompt);
      const nonUserAuthoredPrompts = action.payload.prompts.filter(
        (prompt) => !isUserPrompt(prompt)
      );
      const allPrompts = [...userAuthoredPrompts, ...nonUserAuthoredPrompts];

      return {
        ...state.common,
        allPrompts,
      };
    }
    case "SET_FILTERS":
      return {
        ...state.common,
        filters: action.payload.filters,
      };
    case "UPDATE_SELECTED_TAB":
      return {
        ...state.common,
        selectedTab: action.payload.selectedTab,
      };
    case "DELETE_UNDO": {
      const allPrompts = [...state.common.allPrompts];

      // make sure the prompt is not already in the list
      if (
        allPrompts.findIndex(
          (prompt) => prompt.Id === action.payload.prompt.Id
        ) === -1
      ) {
        allPrompts.splice(
          action.payload.promptPosition,
          0,
          action.payload.prompt
        );
      }

      return {
        ...state.common,
        allPrompts,
      };
    }
    case "DELETE_SUCCEED": {
      const allPrompts = [...state.common.allPrompts].filter(
        (prompt) => prompt.Id !== action.payload.prompt.Id
      );
      return {
        ...state.common,
        allPrompts,
      };
    }
    case "SET_LOADING":
      return {
        ...state.common,
        isLoading: action.payload.isLoading,
      };
    default:
      return state.common;
  }
};

export const rootReducer = (
  state: PromptLibraryState,
  action: PromptLibraryAction
): PromptLibraryState => {
  return {
    common: commonReducer(state, action as CommonAction),
    suggested: suggestedPromptsReducer(state, action as SuggestedPromptsAction),
    saved: savedPromptsReducer(state, action as SavedPromptsAction),
    team: teamPromptsReducer(state, action as TeamPromptsAction),
  };
};

export const PromptLibraryDispatchContext =
  React.createContext<React.Dispatch<PromptLibraryAction> | null>(null);
export function usePromptLibraryDispatch(): React.Dispatch<PromptLibraryAction> {
  const context = React.useContext(PromptLibraryDispatchContext);
  if (!context) {
    throw new Error("usePromptLibraryDispatch is undefined");
  }
  return context;
}

export const PromptLibraryContext =
  React.createContext<PromptLibraryState | null>(null);

export function usePromptLibraryContext(): PromptLibraryState {
  const context = React.useContext(PromptLibraryContext);
  if (!context) {
    throw new Error("usePromptLibraryContextV2 is undefined");
  }
  return context;
}
